import { Button } from '@material-ui/core';
import { useCameraViewContext } from '../../context';
import { useCallback } from 'react';
import { useField } from 'react-final-form';
import styled from 'styled-components';
import { useFieldArray } from 'react-final-form-arrays';
import { SyncManager } from 'db';
import { useVideoUpload } from '../../hooks/useVideoUpload';
import { SaveImageFn, useUpdateImage } from 'pages/ListerVehicleUploadV2/hooks/useUpdateImage';
import { useFormVehicleId } from 'pages/ListerVehicleUploadV2/hooks/useFormVehicleId';
import { UploadStatus, useVideoUploadStatus } from 'services/listerUploadVehicle/v2';
import toast from 'react-hot-toast';
import { useNonBlockingImageUpload } from 'pages/ListerVehicleUploadV2/hooks/useNonBlockingImageUpload';
import { useIsOnline } from 'hooks';
import { useInfoToast } from 'components/InfoToast/useInfoToast';

const Wrapper = styled.div`
  padding: 0px 16px;
`;

const StyledButton = styled(Button)`
  border-radius: 6px;
  text-transform: none;
  width: 100%;
  height: 44px;

  &:disabled {
    background-color: #005a96;
    color: #ffffff;
    opacity: 0.5;

    &:hover {
      background-color: #005a96;
      color: #ffffff;
      opacity: 0.5;
    }
  }

  &:hover {
    background-color: #005a96;
    color: #ffffff;
  }
`;

type TakeNextOptions = {
  switchToCameraView?: boolean;
};

export const useTakeNextHandlerLegacy = ({ switchToCameraView = true }: TakeNextOptions = {}) => {
  const {
    imageLoading,
    setImageLoading,
    level1SectionId,
    closeCameraView,
    setIndex,
    changeIndex,
    currentImageSrc,
    imageIndex,
    isVideoMode,
    recordedChunks,
    isRecording
  } = useCameraViewContext();

  const { fields: images } = useFieldArray(`${level1SectionId}.images`, {
    subscription: { value: true, length: true }
  });

  const vehicleId = useFormVehicleId();

  const { input: urlInput } = useField(`${level1SectionId}.images.${imageIndex}.url`);
  const { input: idInput } = useField(`${level1SectionId}.images.${imageIndex}.id`);

  const {
    input: { value: notes }
  } = useField(`${level1SectionId}.images.${imageIndex}.notes`);
  const {
    input: { value: grade }
  } = useField(`${level1SectionId}.images.${imageIndex}.grade`);
  const {
    input: { value: isDamaged }
  } = useField(`${level1SectionId}.images.${imageIndex}.is_damaged`);

  const uploadVideo = useVideoUpload(level1SectionId!);
  const updateImage = useUpdateImage(level1SectionId!, imageIndex);

  const uploadStatus = useVideoUploadStatus(vehicleId);

  const handleNext = useCallback(async () => {
    if (imageLoading) {
      return { loading: true };
    }

    if (isVideoMode) {
      if (recordedChunks.length === 0 || isRecording) {
        toast.error('Nothing to upload');
        return;
      }

      if (uploadStatus === UploadStatus.IN_PROGRESS || uploadStatus === UploadStatus.QUEUED) {
        toast.error('Video upload is in progress');
        return;
      }

      setImageLoading(true);
      await uploadVideo(recordedChunks);
      setImageLoading(false);
      return;
    }

    if (!currentImageSrc && urlInput.value) {
      if (!switchToCameraView) {
        return;
      }
      return setIndex(images.length || 0);
    }

    const isTiresSection = level1SectionId?.includes('tires');
    const isInteriorSection = level1SectionId?.includes('interior');
    const isUnderhoodSection = level1SectionId?.includes('under_hood');
    const isDriveCyclesSection = level1SectionId?.includes('drive_cycles');
    const withType = isTiresSection || isInteriorSection || isDriveCyclesSection || isUnderhoodSection;

    if (idInput.value && urlInput.value && !withType) {
      if (switchToCameraView) {
        return changeIndex();
      } else {
        return;
      }
    }

    const payload: Parameters<SaveImageFn>[0] = {};

    payload.file = currentImageSrc;
    payload.is_damaged = !!isDamaged;
    if (grade) {
      payload.grade = grade;
    }
    if (notes?.message) {
      payload.notes = notes?.message;
    }

    console.log(vehicleId, 'vehicleId');

    const syncManager = SyncManager.getInstance(vehicleId);

    setImageLoading(true);

    await updateImage(payload);
    await syncManager.waitUntilSynced();

    setImageLoading(false);

    if (switchToCameraView) {
      changeIndex();
    }
  }, [closeCameraView, currentImageSrc, urlInput, images.length, imageLoading, setImageLoading, uploadStatus]);

  return handleNext;
};

export const useTakeNextHandler = ({ switchToCameraView = true }: TakeNextOptions = {}) => {
  const vehicleId = useFormVehicleId();

  const fireInfoToast = useInfoToast();

  const formatSyncPayload = ({ skipFile = false }) => {
    const payload: Parameters<SaveImageFn>[0] = {};

    if (!skipFile) {
      payload.file = currentImageSrc;
    }
    payload.is_damaged = !!isDamaged;
    if (grade) {
      payload.grade = grade;
    }
    if (notes?.message) {
      payload.notes = notes?.message;
    }
    if (expirationDate) {
      payload.expiration_date = expirationDate;
    }

    return payload;
  };

  const {
    currentImageSrc,
    level1SectionId,
    imageIndex,
    changeIndex,
    setIndex,
    isVideoMode,
    recordedChunks,
    isRecording,
    setImageLoading,
    imageLoading
  } = useCameraViewContext();
  const nonBlockingUpload = useNonBlockingImageUpload(level1SectionId!, imageIndex);
  const { fields: images } = useFieldArray(`${level1SectionId}.images`);

  const updateImage = useUpdateImage(level1SectionId!, imageIndex);

  const uploadVideo = useVideoUpload(level1SectionId!);

  const { input: urlInput } = useField(`${level1SectionId}.images.${imageIndex}.url`);
  const {
    input: { value: expirationDate }
  } = useField(`${level1SectionId}.images.${imageIndex}.expiration_date`);
  const {
    input: { value: isDamaged }
  } = useField(`${level1SectionId}.images.${imageIndex}.is_damaged`);
  const {
    input: { value: notes }
  } = useField(`${level1SectionId}.images.${imageIndex}.notes`);
  const {
    input: { value: grade }
  } = useField(`${level1SectionId}.images.${imageIndex}.grade`);

  const uploadStatus = useVideoUploadStatus(vehicleId);

  const isOnline = useIsOnline();

  return async () => {
    if (imageLoading) {
      return { loading: true };
    }

    if (isVideoMode) {
      if (recordedChunks.length === 0 || isRecording) {
        toast.error('Nothing to upload');
        return;
      }

      if (uploadStatus === UploadStatus.IN_PROGRESS || uploadStatus === UploadStatus.QUEUED) {
        toast.error('Video upload is in progress');
        return;
      }

      setImageLoading(true);
      fireInfoToast('Uploading to the server...');
      await uploadVideo(recordedChunks);
      setImageLoading(false);
      return;
    }

    if (!currentImageSrc && urlInput.value) {
      const payload = formatSyncPayload({ skipFile: true });
      await updateImage(payload);
      if (!switchToCameraView) {
        return;
      }
      return setIndex(images.length || 0);
    }

    if (isOnline) {
      nonBlockingUpload(currentImageSrc!);
    } else {
      const payload = formatSyncPayload({ skipFile: false });
      await updateImage(payload);
    }

    if (switchToCameraView) {
      changeIndex();
    }
  };
};

export const TakeNextButton = () => {
  const label = 'Next';
  const handleNext = useTakeNextHandler();
  const { level1SectionId, imageLoading, imageIndex } = useCameraViewContext();

  if (level1SectionId === 'drive_cycles' || imageIndex === 49) {
    return null;
  }

  return (
    <Wrapper>
      <StyledButton disabled={imageLoading} color="primary" variant="contained" onClick={handleNext}>
        {label}
      </StyledButton>
    </Wrapper>
  );
};

export const SaveVideoButton = () => {
  const label = 'Save Video';
  const handleNext = useTakeNextHandler();
  const { level1SectionId, imageLoading, setIndex } = useCameraViewContext();

  const { fields } = useFieldArray(`${level1SectionId}.images`);

  const handleClick = async () => {
    await handleNext();
    setIndex(fields.length!);
  };

  return (
    <Wrapper>
      <StyledButton disabled={imageLoading} color="primary" variant="contained" onClick={handleClick}>
        {label}
      </StyledButton>
    </Wrapper>
  );
};
